import React, { useState } from 'react';
import {
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

const styles = {
  form: {
    maxWidth: '500px',
    margin: '0 auto',
  },
  paymentElement: {
    marginBottom: '1.5rem',
  },
  orderSummary: {
    backgroundColor: 'var(--bg-neutral-alt, #f3f4f6)',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    border: '1px solid var(--border-color, #e1e5e9)',
  },
  summaryTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    color: 'var(--text-color, #333)',
  },
  summaryLine: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.25rem',
    fontSize: '0.95rem',
    color: 'var(--text-light, #666)',
  },
  summaryTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: '0.5rem',
    borderTop: '1px solid var(--border-color, #e1e5e9)',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: 'var(--text-color, #333)',
  },
  submitButton: {
    width: '100%',
    backgroundColor: 'var(--accent-color, #007bff)',
    color: 'white',
    border: 'none',
    padding: '1rem 2rem',
    borderRadius: '8px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    marginTop: '1rem',
  },
  submitButtonDisabled: {
    backgroundColor: 'var(--button-disabled, #ccc)',
    cursor: 'not-allowed',
  },
  submitButtonProcessing: {
    backgroundColor: 'var(--accent-secondary, #28a745)',
  },
  message: {
    textAlign: 'center',
    marginTop: '1rem',
    padding: '0.75rem',
    borderRadius: '6px',
  },
  success: {
    backgroundColor: 'var(--success-bg, #d4edda)',
    color: 'var(--success-color, #155724)',
    border: '1px solid var(--success-border, #c3e6cb)',
  },
  error: {
    backgroundColor: 'var(--error-bg, #f8d7da)',
    color: 'var(--error-color, #721c24)',
    border: '1px solid var(--error-border, #f5c6cb)',
  },
  processingText: {
    fontSize: '0.9rem',
    color: 'var(--text-light, #666)',
    textAlign: 'center',
    marginTop: '0.5rem',
  }
};

export default function CheckoutForm({ onSuccess, packageInfo }) {
  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      return;
    }

    setIsLoading(true);
    setMessage(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url: `${window.location.origin}/purchase-success`,
      },
      redirect: 'if_required'
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error) {
      if (error.type === "card_error" || error.type === "validation_error") {
        setMessage(error.message);
      } else {
        setMessage("An unexpected error occurred.");
      }
    } else {
      // Payment succeeded
      setMessage("Payment successful! Your unlocks have been added to your account.");
      
      // Call the success callback after a short delay to show the success message
      setTimeout(() => {
        onSuccess();
      }, 2000);
    }

    setIsLoading(false);
  };

  const paymentElementOptions = {
    layout: "tabs"
  };

  return (
    <form style={styles.form} onSubmit={handleSubmit}>
      <div style={styles.orderSummary}>
        <h3 style={styles.summaryTitle}>Order Summary</h3>
        <div style={styles.summaryLine}>
          <span>{packageInfo.name}</span>
          <span>${packageInfo.price}</span>
        </div>
        <div style={styles.summaryTotal}>
          <span>Total</span>
          <span>${packageInfo.price}</span>
        </div>
      </div>

      <div style={styles.paymentElement}>
        <PaymentElement options={paymentElementOptions} />
      </div>

      <button 
        disabled={isLoading || !stripe || !elements} 
        style={{
          ...styles.submitButton,
          ...(isLoading ? styles.submitButtonProcessing : {}),
          ...((isLoading || !stripe || !elements) ? styles.submitButtonDisabled : {})
        }}
        type="submit"
      >
        <span>
          {isLoading ? "Processing..." : `Pay $${packageInfo.price}`}
        </span>
      </button>

      {isLoading && (
        <div style={styles.processingText}>
          Please wait while we process your payment...
        </div>
      )}

      {/* Show any error or success messages */}
      {message && (
        <div style={{
          ...styles.message,
          ...(message.includes('successful') ? styles.success : styles.error)
        }}>
          {message}
        </div>
      )}
    </form>
  );
} 